import { useCallback, useEffect, useState } from "react";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import renderStatusBadge from "../../utils/badges";
import { getMasterTenantLogsAction } from "../../Actions/TenantActions";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import DataTable from "../../components/DataTable/DataTable";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	Divider,
	IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setMonth(start.getMonth() - 1);

	return { from: start, to: end };
};

/* ---------- Design tokens ---------- */
const C = {
	primary: "#2563EB",
	primaryDark: "#1D4ED8",
	primarySoft: "#EFF6FF",
	border: "#E5E7EB",
	text: "#111827",
	bg: "#F9FAFB",
	white: "#FFFFFF",
	codeBg: "#0F172A",
	codeText: "#E2E8F0",
};

const fontStack = "var(--app-font)";

const CodeBlock = ({ value }) => {
	let displayValue = value;
	try {
		if (typeof value === 'string') {
			const parsed = JSON.parse(value);
			displayValue = JSON.stringify(parsed, null, 2);
		} else {
			displayValue = JSON.stringify(value, null, 2);
		}
	} catch {
		displayValue = value;
	}

	return (
		<Box
			component="pre"
			sx={{
				m: 0,
				p: 2,
				bgcolor: C.codeBg,
				color: C.codeText,
				borderRadius: "10px",
				fontFamily:
					'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
				fontSize: 12.5,
				lineHeight: 1.6,
				maxHeight: 400,
				overflow: "auto",
				whiteSpace: "pre-wrap",
				wordBreak: "break-word",
			}}
		>
			{displayValue || "—"}
		</Box>
	);
};

const MasterTenantLogModal = ({ open, onClose, data }) => {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			PaperProps={{
				sx: {
					borderRadius: "16px",
					overflow: "hidden",
					fontFamily: fontStack,
					boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
				},
			}}
		>
			<DialogTitle
				sx={{
					p: 0,
					background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
					color: C.white,
				}}
			>
				<Box
					sx={{
						px: 3,
						py: 2.25,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: "10px",
								bgcolor: "rgba(255,255,255,0.15)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
						</Box>
						<Box>
							<Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
								Tenant Response Details
							</Typography>
							<Typography sx={{ fontSize: 12, opacity: 0.8 }}>
								Full response from the master tenant log
							</Typography>
						</Box>
					</Box>
					<IconButton
						onClick={onClose}
						sx={{ color: C.white, "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent sx={{ p: 0, bgcolor: C.bg }}>
				<Box sx={{ p: 3 }}>
					<Box
						sx={{
							bgcolor: C.white,
							border: `1px solid ${C.border}`,
							borderRadius: "12px",
							p: 2.25,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
							<Box
								sx={{
									width: 28,
									height: 28,
									borderRadius: "8px",
									bgcolor: C.primarySoft,
									color: C.primary,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
							</Box>
							<Typography
								sx={{
									fontFamily: fontStack,
									fontSize: 14,
									fontWeight: 600,
									color: C.text,
									letterSpacing: 0.2,
								}}
							>
								Response Data
							</Typography>
						</Box>

						<CodeBlock value={data?.tenant_response || ""} />
					</Box>
				</Box>
			</DialogContent>

			<Divider sx={{ borderColor: C.border }} />
			<DialogActions sx={{ px: 3, py: 2, bgcolor: C.white }}>
				<Button
					onClick={onClose}
					variant="contained"
					disableElevation
					sx={{
						textTransform: "none",
						fontFamily: fontStack,
						fontWeight: 600,
						fontSize: 14,
						px: 3,
						py: 1,
						borderRadius: "10px",
						bgcolor: C.primary,
						"&:hover": { bgcolor: C.primaryDark },
					}}
				>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};


const MasterMailLogsSection = () => {
	const [logsData, setLogsData] = useState([]);
	const [selectedLog, setSelectedLog] = useState(null);
	const defaultRange = getDefaultDateRange();
	const [filterValues, setFilterValues] = useState({
		status: "all",
		search: "",
		dateRange: defaultRange,
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [loading, setLoading] = useState(true);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const startDate = filterValues.dateRange?.from
		? new Date(
			Date.UTC(
				filterValues.dateRange.from.getFullYear(),
				filterValues.dateRange.from.getMonth(),
				filterValues.dateRange.from.getDate(),
				0,
				0,
				0,
				0
			)
		).toISOString()
		: null;

	const endDate = filterValues.dateRange?.to
		? new Date(
			Date.UTC(
				filterValues.dateRange.to.getFullYear(),
				filterValues.dateRange.to.getMonth(),
				filterValues.dateRange.to.getDate(),
				23,
				59,
				59,
				999
			)
		).toISOString()
		: null;

	const fetchTenantLogs = useCallback(
		async (isFilterAction = false) => {
			setLoading(true);
			try {
				const res = await getMasterTenantLogsAction(
					currentPage,
					rowsPerPage,
					filterValues.status,
					startDate,
					endDate,
					filterValues.search

				);

				setLogsData(res.data.data.rows);
				setTotalRecords(res.data.data.count);
			} catch (error) {
				console.error("Fetch error:", error);
				setSnackbar({
					open: true,
					message: error?.response?.data?.message || "Failed to fetch tenant logs",
					severity: "error",
				});
			} finally {
				setLoading(false);
			}
		},
		[currentPage, rowsPerPage, filterValues]
	);

	useEffect(() => {
		fetchTenantLogs();
	}, [fetchTenantLogs]);

	const handleFilterApply = (values) => {
		setCurrentPage(1);
		setFilterValues(values);
	};

	const handleFilterReset = () => {
		setCurrentPage(1);

		setFilterValues({
			status: "all",
			search: "",
			dateRange: defaultRange,
		});
	};

	const columns = [
		{
			field: "tenant_name",
			headerName: "Tenant Name",
			flex: 1,
			minWidth: 150,
		},
		{
			field: "domain_url",
			headerName: "Domain URL",
			flex: 1,
			minWidth: 200,
		},
		{
			field: "status",
			headerName: "Status",
			minWidth: 120,
			renderCell: ({ row }) =>
				renderStatusBadge(
					{
						SUCCESS: { title: "Success", status: "success" },
						FAILED: { title: "Failed", status: "danger" },
						PENDING: { title: "Pending", status: "warning" },
					}[row.status]
				),
		},
		{
			field: "created_At",
			headerName: "Date And Time",
			minWidth: 200,
			renderCell: ({ row }) => formatDateTime(row.created_At, true),
		},
	];


	const filters = [
		{
			name: "search",
			type: "search",
			placeholder: "Search by tenant name, domain or response",
		},
		{
			name: "status",
			label: "Status",
			type: "select",
			options: [
				{ label: "All Status", value: "all" },
				{ label: "Success", value: "SUCCESS" },
				{ label: "Failed", value: "FAILED" },
				{ label: "Pending", value: "PENDING" },
			],
		},
		{
			name: "dateRange",
			label: "Date Range",
			type: "dateRange",
		},
	];

	return (
		<>
			<div id="kt_content_container">
				<BreadcrumbsComponent />

				<DataTable
					rows={logsData}
					columns={columns}
					loading={loading}
					totalCount={totalRecords}
					page={currentPage}
					pageSize={rowsPerPage}
					getRowId={(row) => row.id}
					filters={filters}
					filterValues={filterValues}
					onFilterApply={handleFilterApply}
					onFilterReset={handleFilterReset}
					onPageChange={setCurrentPage}
					onPageSizeChange={(size) => {
						setRowsPerPage(size);
						setCurrentPage(1);
					}}
					showPagination={true}
					showRowsPerPage={true}
					actions={{ view: true }}
					onAction={(type, row) => {
						if (type === "view") setSelectedLog(row);
					}}
				/>

				<CustomSnackbar
					{...snackbar}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
				/>

				<MasterTenantLogModal
					open={Boolean(selectedLog)}
					onClose={() => setSelectedLog(null)}
					data={selectedLog}
				/>
			</div>
		</>
	);
};

export default MasterMailLogsSection;